import { useState, useCallback } from 'react';

const KEY = 'shariah_watchlist';
const MAX = 30;

function load() {
  try { return JSON.parse(localStorage.getItem(KEY)) || []; }
  catch { return []; }
}

function persist(items) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
}

export function useWatchlist() {
  const [items, setItems] = useState(load);

  const add = useCallback((ticker, companyName, verdict, price) => {
    setItems(prev => {
      if (prev.find(i => i.ticker === ticker)) return prev;
      const next = [
        { ticker, companyName, verdict, price: price || null, savedAt: new Date().toISOString() },
        ...prev,
      ].slice(0, MAX);
      persist(next);
      return next;
    });
  }, []);

  const remove = useCallback((ticker) => {
    setItems(prev => {
      const next = prev.filter(i => i.ticker !== ticker);
      persist(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    persist([]);
  }, []);

  // Silently update verdict + name + price if ticker is already saved (called after every screen)
  const update = useCallback((ticker, companyName, verdict, price) => {
    setItems(prev => {
      if (!prev.find(i => i.ticker === ticker)) return prev;
      const next = prev.map(i =>
        i.ticker === ticker
          ? { ...i, companyName, verdict, price: price || i.price, updatedAt: new Date().toISOString() }
          : i
      );
      persist(next);
      return next;
    });
  }, []);

  const has = useCallback((ticker) => items.some(i => i.ticker === ticker), [items]);

  const toggle = useCallback((ticker, companyName, verdict, price) => {
    setItems(prev => {
      const exists = prev.find(i => i.ticker === ticker);
      const next = exists
        ? prev.filter(i => i.ticker !== ticker)
        : [{ ticker, companyName, verdict, price: price || null, savedAt: new Date().toISOString() }, ...prev].slice(0, MAX);
      persist(next);
      return next;
    });
  }, []);

  return { items, add, remove, clear, update, has, toggle };
}
